import React, { FC, useMemo } from 'react';

import { Divider, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { Box, Stack } from '@mui/system';

import {
  AdminAllowanceNames,
  AllowancesNames,
  ContractTypes,
  convertToDecimals,
  currency,
  decimalMaxLength,
  getLongTermContractAttributes,
} from '@pharmaplan/common';
import { getDaysDistribution } from '@pharmaplan/common/helpers/utils';

import { useAppSelector } from '../../../../../hooks/useAppSelector';
import CustomTextField from '../../../../common/CustomTextField';
import { Constants } from '../../../../../helpers/Constants';
import strings from '../../../../../localization';

import classes from './styles';

interface ILongTermHourlyRates {
  formik: FormikProps<any>;
  customWeekHours?: number;
  customWeekendHours?: number;
  CustomContent?: React.ReactNode;
}

const LongTermHourlyRates: FC<ILongTermHourlyRates> = ({
  formik,
  customWeekHours,
  customWeekendHours,
  CustomContent,
}) => {
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { startDate, endDate, shiftContractAttributes } = longTermAttributes ?? {};

  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
  } = formik.values ?? {};

  const { weekdayHours, weekendHours } = useMemo(() => {
    if (
      (customWeekHours || customWeekHours === 0)
      && (customWeekendHours || customWeekendHours === 0)
    ) {
      return {
        weekdayHours: customWeekHours,
        weekendHours: customWeekendHours,
      };
    }
    return getDaysDistribution(shiftContractAttributes);
  }, [customWeekHours, customWeekendHours, shiftContractAttributes]);

  const weekdayTotalRate = weekDayPaidHourlyRate * weekdayHours;
  const weekendTotalRate = weekendPaidHourlyRate * weekendHours;
  const pharmacyWeekdayTotalRate = pharmacyWeekDayHourlyRate * weekdayHours;
  const pharmacyWeekendTotalRate = pharmacyWeekendHourlyRate * weekendHours;

  const total = `${currency}${convertToDecimals(
    weekdayTotalRate
      + weekendTotalRate
      + pharmacyWeekdayTotalRate
      + pharmacyWeekendTotalRate,
  )} + Tx ${strings.and} ${strings.otherFees}`;

  const showCalculation = startDate && endDate;
  const showWeekday = weekDayPaidHourlyRate && weekdayHours;
  const showWeekend = weekendPaidHourlyRate && weekendHours;

  const showPharmacyWeekday = pharmacyWeekDayHourlyRate && weekdayHours;
  const showPharmacyWeekend = pharmacyWeekendHourlyRate && weekendHours;

  const fieldConfig = [
    {
      label: strings.formatString(strings.userWeekdayRate, strings.pharmacist),
      name: AllowancesNames.weekDayPaidHourlyRate,
      id: AllowancesNames.weekDayPaidHourlyRate,
      disabled: !weekdayHours,
    },
    {
      label: strings.formatString(strings.userWeekendRate, strings.pharmacist),
      name: AllowancesNames.weekendPaidHourlyRate,
      id: AllowancesNames.weekendPaidHourlyRate,
      disabled: !weekendHours,
    },

    {
      label: strings.formatString(strings.userWeekdayRate, strings.pharmacy),
      name: AdminAllowanceNames.pharmacyWeekDayHourlyRate,
      id: AdminAllowanceNames.pharmacyWeekDayHourlyRate,
      disabled: !weekdayHours,
    },
    {
      label: strings.formatString(strings.userWeekendRate, strings.pharmacy),
      name: AdminAllowanceNames.pharmacyWeekendHourlyRate,
      id: AdminAllowanceNames.pharmacyWeekendHourlyRate,
      disabled: !weekendHours,
    },
  ];

  const calculationConfig = [
    {
      label: strings.weekday,
      value: `${currency}${weekDayPaidHourlyRate} X ${weekdayHours} = ${currency}${convertToDecimals(
        weekdayTotalRate,
      )}`,
      show: !!showWeekday,
    },
    {
      label: strings.weekend,
      value: `${currency}${weekendPaidHourlyRate} X ${weekendHours} = ${currency}${convertToDecimals(
        weekendTotalRate,
      )}`,
      show: !!showWeekend,
    },
    {
      label: strings.formatString(strings.userWeekday, strings.pharmacy),
      value: `${currency}${pharmacyWeekDayHourlyRate} X ${weekdayHours} = ${currency}${convertToDecimals(
        pharmacyWeekdayTotalRate,
      )}`,
      show: !!showPharmacyWeekday,
    },
    {
      label: strings.formatString(strings.userWeekend, strings.pharmacy),
      value: `${currency}${pharmacyWeekendHourlyRate} X ${weekendHours} = ${currency}${convertToDecimals(
        pharmacyWeekendTotalRate,
      )}`,
      show: !!showPharmacyWeekend,
    },
  ];

  if (formik.values.contractType === ContractTypes.shortTerm) return null;

  return (
    <Stack>
      <Typography sx={classes.title}>{strings.rateAndAllowances}</Typography>

      {fieldConfig.map((item) => {
        const { label, name, id, disabled } = item;
        return (
          <CustomTextField
            label={label as string}
            name={name}
            formik={formik}
            disabled={disabled}
            placeholder={Constants.allowancePlaceholder}
            suffix={Constants.priceSuffix}
            maxLength={decimalMaxLength}
            customClass={classes.hourlyRate}
            id={id}
            horizontal
          />
        );
      })}

      {CustomContent}

      {showCalculation && (
        <Box sx={classes.calculationContainer}>
          <Typography sx={classes.title}>
            {strings.estimatedShiftCost}
          </Typography>
          {calculationConfig.map((item) => {
            const { label, value, show } = item ?? {};

            return (
              show && (
                <Box sx={classes.weekContainer}>
                  <Typography sx={classes.label}>{label}</Typography>
                  <Typography sx={classes.rateValue}>{value}</Typography>
                </Box>
              )
            );
          })}

          <Divider sx={classes.divider} />
          <Box sx={classes.weekContainer}>
            <Typography sx={classes.label}>{strings.total}</Typography>
            <Typography sx={classes.rateValue}>{total}</Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
export default LongTermHourlyRates;
